import { useContext, useState, useEffect} from 'react';

//context
import { ListContext } from '@/context/ListContext';

//interfaces
import { ISearchContentItem } from  '@/interfaces/content';
import { ITableContent, ITableRows } from  '@/interfaces/simpleTable';
import { NotificationStatus } from  '@/interfaces/languages';
import { INotificationDetails } from  '@/interfaces/toasts';

//components
import { getBasicContentRow, getSearchContentRow, IContentItem } from  '@/helpers/ContentTable';

//api
import { duplicateContentInList, getAllMenuItems, getMainMenuId, searchContent} from '@/api/lists';

//hooks
import { useTimer } from  '@/components/hooks/utility/useTimer';
import UseBasicBreadcrumbsForContentModals from  '@/components/hooks/contentModals/BasicBreadcrumbsForContentModals.hooks';


export interface ISelectContentTable {simpleTableContent: ITableContent, getMoreContent: any, isLoading: boolean, infiniteScrollFetchCondition: boolean, noDataMessage: string};

const UseContentModal = ({setShowModal, isDuplicateContent}:{setShowModal:any, isDuplicateContent?: boolean}) => {
    const { listId, appId, appGuid } = useContext(ListContext)
    const { breadcrumbs, setBreadcrumb } = UseBasicBreadcrumbsForContentModals({ onBreadcrumbClick });
    const [abortController, setAbortController] = useState<AbortController>();
    const [menuId, setMenuId] = useState<number>();
    const [searchString, setSearchString] = useState<string>("");
    const [selectedContentId, setSelectedContentId] = useState<number>();
    const [isLoading, setIsLoading]=useState<boolean>(false);
    const [contentListId, setContentListId] = useState<number>();
    const [moreContentAvailable, setMoreContentAvailable]=useState<boolean>(false);
    const [skip, setSkip] = useState<number>(0);
    const [moreSearchContentAvailable, setMoreSearchContentAvailable]=useState<boolean>(false);
    const [searchSkip, setSearchSkip] = useState<number>(0);
    const [contentData, setContentData] = useState<IContentItem[]>([]);
    const [searchContentData, setSearchContentData] = useState<ISearchContentItem[]>([]);
    const [notificationDetails, setNotificationDetails] = useState<INotificationDetails>({ show: false, status: NotificationStatus.null, message: "" });
    const [confirmButtonClicked, setConfirmButtonClicked] = useState<boolean>(false);
    const tableColumns = [{title: "Title", isToggleButton: false}, {title: "Actions", isToggleButton: false}]
    const showBreadcrumbs:boolean = !searchString;
    const showBackButton:boolean = breadcrumbs.length > 1 && !searchString;
    const textInputDisabled:boolean = !menuId;
    const modalTitle:string = isDuplicateContent ? "Duplicate a page" : "Add an existing content item"
    const confirmButtonText:string = isDuplicateContent ? "Create Duplicate" : "Add Content";
    const confirmButtonLabel:string = isDuplicateContent ? "Create Duplication of the selected page" : "Add existing content item";
    const [tableData, setTableData]=useState<ISelectContentTable>({
        simpleTableContent: { columns: tableColumns, rows: tableRows() },
        getMoreContent: getMoreContent,
        isLoading: isLoading,
        infiniteScrollFetchCondition: searchString ? moreSearchContentAvailable : moreContentAvailable,
        noDataMessage: menuId ? "No content available" : "Something went wrong"
    });
    
    useEffect(()=> {
        getMenuId();
        (window as any)?.functions?.closeWidgetPanel?.();
    }, []);

    useEffect(()=> {
        if (menuId) {
            setContentListId(menuId);
            setBreadcrumb(menuId, "Menu");
        }
    }, [menuId]);

    useEffect(()=> {
        getContent();
    }, [contentListId]);

    useEffect(()=> {
        if (searchString) {
            getContentWithSearchTerm();
        } else {
            getContent();
        }
    }, [searchString]);

    useEffect(() => {
        setTableData({
            simpleTableContent: { columns: tableColumns, rows: tableRows() },
            getMoreContent: getMoreContent,
            infiniteScrollFetchCondition: searchString ? moreSearchContentAvailable : moreContentAvailable,
            isLoading: isLoading,
            noDataMessage: menuId ? "No content available" : "Something went wrong",
        });
    }, [contentData, searchContentData, isLoading, moreContentAvailable, selectedContentId]);

    useTimer(
        notificationDetails?.show ?? false,
        () => setNotificationDetails({ show: false, status: NotificationStatus.null, message: "" }),
        3000
    );

    useTimer(
        notificationDetails.status === NotificationStatus.success,
        () => {
            setShowModal(false), 
            (window as any)?.functions?.refreshMainMenu();
        },
        1500
    );

    async function getContentWithSearchTerm(isPagination?: boolean): Promise<void> {
        if (isDuplicateContent) {
            if (abortController) { abortController.abort() }
            const newAbortController = new AbortController();
            setAbortController(newAbortController);

            searchContent(appGuid, searchString, isPagination ? searchSkip : 0, { signal: newAbortController.signal })
            .then((data) => {
                if (isPagination) {
                    setSearchContentData((prevTableData) => [...(prevTableData || []), ...data.items]);
                } else {
                    setSearchContentData(data.items);
                }
                setSearchSkip(data.nextSkip ?? 0);
                setMoreSearchContentAvailable(Boolean(data.nextSkip));
            })
            .catch((error)=> {
                if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
                    setNotificationDetails({show: true, status: NotificationStatus.error, message:"There was a problem searching content. Please try again."});
                }
            })
        } else {
            //implement when it is add existing content
        }
    }
    
    
    async function getContent(pagination?: boolean): Promise<void> {
        if (!pagination) { setIsLoading(true);}
        if (!contentListId) { return }

        const top = 20;

        if (isDuplicateContent) {
            getAllMenuItems(appId, contentListId?.toString(), top, pagination ? skip + top : 0, false)
                .then((data) => {
                    if (pagination) {
                        setContentData((prevTableData) => [...(prevTableData || []), ...data.records]);
                    } else {
                        setContentData(data.records);
                    }
                    setSkip(data.skip);
                    setMoreContentAvailable(data.moreAvailable);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
           //implement when it is add existing content
        }
    }

    async function getMenuId(): Promise<void> {
        getMainMenuId(appId)
        .then((data) => {
            setMenuId(data?.mainMenuId);
        })
    }

    function getMoreContent() {
        if (searchString) {
            getContentWithSearchTerm(true);
        } else {
            getContent(true);
        }
    }

    async function onConfirmClick(): Promise<void> {
        if (!selectedContentId) return 
        setConfirmButtonClicked(true);
        duplicateContentInList(appId, selectedContentId, listId)
        .then(() => {
            setNotificationDetails({show: true, status: NotificationStatus.success, message:"Successfully duplicated content."});
        })
        .catch(() => {
            setNotificationDetails({show: true, status: NotificationStatus.error, message:"There was a problem duplicating the content. Please try again."});
            setConfirmButtonClicked(false);
        })
    }

    function onOpenListClick(id: number, title: string) {
        setContentListId(id);
        setBreadcrumb(id, title);
    }

    function onBackButtonClick() {
        const prevBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
        if (prevBreadcrumb?.onClick) {
            prevBreadcrumb.onClick();
        }
        setContentListId(prevBreadcrumb.contentId);
    }

    function onBreadcrumbClick(id: number, title: string) {
        setIsLoading(true);
        setContentListId(id);
        setBreadcrumb(id, title);
    }

    function tableRows() {
        if (searchString.length > 0) {
            return searchContentData
                .map((content) => getSearchContentRow(appGuid, content, setSelectedContentId, selectedContentId))
                .filter((row): row is ITableRows => row !== undefined);
        } else {
            return contentData
                .map((content) => getBasicContentRow(appGuid, content, onOpenListClick, setSelectedContentId, selectedContentId))
                .filter((row): row is ITableRows => row !== undefined);
        }
    }

    return {
        modalTitle,
        confirmButtonClicked,
        confirmButtonText,
        confirmButtonLabel,
        searchString,
        breadcrumbs,
        tableData,
        showBreadcrumbs,
        showBackButton,
        selectedContentId,
        notificationDetails,
        textInputDisabled,
        onBackButtonClick,
        setSearchString,
        onConfirmClick,
    }
}
    
export default UseContentModal

